import { supabase } from '@/supabase.config';
import { MONTH_NAMES, lastDayOfMonthISO } from '../types';
import type { TitheSubjectType } from '../types';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const db = supabase as any;

export interface PersonReportSubject {
  subjectType: TitheSubjectType;
  subjectId: string;
  /** Position rows: reports follow the minister across every position they held. */
  ministerId?: string;
  label: string;
  sublabel: string;
  roleLabel: string;
}

interface Tenure { started_at: string; ended_at: string | null }
interface PaidRow { year: number; month: number; amount: number; paid_at: string }

/**
 * Two small queries, only when the user asks for a report: the subject's tenures
 * (positions only) and their tithes within the year range.
 */
async function loadRecord(subject: PersonReportSubject, fromYear: number, toYear: number) {
  let tenures: Tenure[] | null = null;
  let tithesQuery = db.from('tithes').select('year, month, amount, paid_at').gte('year', fromYear).lte('year', toYear);

  if (subject.subjectType === 'position') {
    const { data, error } = await db
      .from('authority_positions')
      .select('id, started_at, ended_at')
      .eq(subject.ministerId ? 'minister_id' : 'id', subject.ministerId ?? subject.subjectId);
    if (error) throw error;
    const positions = (data ?? []) as Array<Tenure & { id: string }>;
    tenures = positions;
    tithesQuery = tithesQuery.in('authority_position_id', positions.map((p) => p.id));
  } else {
    const column = { district: 'district_id', region: 'region_id', institution: 'institution_slug' }[subject.subjectType];
    tithesQuery = tithesQuery.eq(column, subject.subjectId).is('authority_position_id', null);
  }

  const { data, error } = await tithesQuery;
  if (error) throw error;
  return { tithes: (data ?? []) as PaidRow[], tenures };
}

function inTenure(tenures: Tenure[] | null, year: number, month: number): boolean {
  if (!tenures) return true;
  const start = `${year}-${String(month).padStart(2, '0')}-01`;
  const end = lastDayOfMonthISO(year, month);
  return tenures.some((t) => t.started_at.slice(0, 10) <= end && (!t.ended_at || t.ended_at.slice(0, 10) >= start));
}

/** Downloads a year-by-month record of one person/entity, with what is paid, late and still pending. */
export async function exportPersonReportExcel(
  subject: PersonReportSubject,
  fromYear: number,
  toYear: number,
): Promise<void> {
  const { tithes, tenures } = await loadRecord(subject, fromYear, toYear);
  const ExcelJS = (await import('exceljs')).default;

  const paid = new Map<string, { amount: number; paidAt: string }>();
  for (const t of tithes) {
    const key = `${t.year}-${t.month}`;
    paid.set(key, { amount: (paid.get(key)?.amount ?? 0) + t.amount, paidAt: t.paid_at });
  }

  const now = new Date();
  let total = 0, paidMonths = 0, pendingMonths = 0, lateMonths = 0;

  const wb = new ExcelJS.Workbook();
  const ws = wb.addWorksheet('Diezmos');
  ws.columns = [{ width: 8 }, ...MONTH_NAMES.map(() => ({ width: 11 })), { width: 14 }];

  const title = ws.addRow([subject.roleLabel ? `${subject.label} — ${subject.roleLabel}` : subject.label]);
  title.font = { bold: true, size: 14 };
  if (subject.sublabel) ws.addRow([`Código: ${subject.sublabel}`]);
  ws.addRow([`Registro de diezmos ${fromYear === toYear ? fromYear : `${fromYear} – ${toYear}`}`]);
  ws.addRow([]);

  const header = ws.addRow(['Año', ...MONTH_NAMES, 'Total Bs.']);
  header.font = { bold: true, color: { argb: 'FFFFFFFF' } };
  header.eachCell((c) => { c.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF1A3A5C' } }; c.alignment = { horizontal: 'center' }; });

  for (let year = fromYear; year <= toYear; year++) {
    const row = ws.addRow([year]);
    let yearTotal = 0;
    for (let month = 1; month <= 12; month++) {
      const cell = row.getCell(month + 1);
      const entry = paid.get(`${year}-${month}`);
      cell.alignment = { horizontal: 'center' };
      if (entry) {
        cell.value = entry.amount;
        cell.numFmt = '#,##0.00';
        yearTotal += entry.amount; total += entry.amount; paidMonths++;
        if (entry.paidAt.slice(0, 10) > lastDayOfMonthISO(year, month)) {
          lateMonths++;
          cell.font = { color: { argb: 'FFC62828' } };
          cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFFDECEA' } };
        }
        continue;
      }
      const elapsed = year < now.getFullYear() || (year === now.getFullYear() && month <= now.getMonth() + 1);
      if (!inTenure(tenures, year, month)) {
        cell.value = '—';
        cell.font = { color: { argb: 'FF9E9E9E' } };
      } else if (elapsed) {
        pendingMonths++;
        cell.value = 'Pendiente';
        cell.font = { color: { argb: 'FF8D6E00' } };
        cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFFFF8E1' } };
      }
    }
    const totalCell = row.getCell(14);
    totalCell.value = yearTotal;
    totalCell.numFmt = '#,##0.00';
    totalCell.font = { bold: true };
  }

  ws.addRow([]);
  const summary = ws.addRow(['Resumen']);
  summary.font = { bold: true };
  ws.addRow([`Meses pagados: ${paidMonths} · Pendientes: ${pendingMonths} · Con retraso: ${lateMonths}`]);
  ws.addRow([`Total pagado: Bs. ${total.toLocaleString('es-BO', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`]);

  const buffer = await wb.xlsx.writeBuffer();
  const blob = new Blob([buffer], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  });
  const url = URL.createObjectURL(blob);
  const a   = document.createElement('a');
  a.href     = url;
  a.download = `diezmos_${subject.label.replace(/[^\p{L}\p{N}]+/gu, '_')}_${fromYear}-${toYear}.xlsx`;
  a.click();
  URL.revokeObjectURL(url);
}
